import { getCatalogDecorationById, getTileUrl, resolveTileId, } from "./tileCatalog.js";
import { resolveTileAssetUrl } from "./tileAssetUrls.js";
import { createOverlayAssetStoragePath } from "../types/catalogIdentifiers.js";
import { getCoastOverlaysForHex } from "./coastOverlays.js";
import { getRoadOverlaysForEdges } from "./roadOverlays.js";
const cache = new Map();
const inflight = new Map();
function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load: ${src}`));
        img.src = src;
    });
}
function ensureImage(cacheKey, src, onReady) {
    const cached = cache.get(cacheKey);
    if (cached) {
        return Promise.resolve(cached);
    }
    const existing = inflight.get(cacheKey);
    if (existing) {
        return existing;
    }
    const promise = loadImage(src)
        .then((img) => {
        cache.set(cacheKey, img);
        inflight.delete(cacheKey);
        onReady?.();
        return img;
    })
        .catch((error) => {
        inflight.delete(cacheKey);
        throw error;
    });
    inflight.set(cacheKey, promise);
    return promise;
}
export async function preloadTiles(tileIds) {
    const unique = [...new Set(tileIds)];
    const promises = unique
        .map((tileId) => ({ tileId, src: getTileUrl(tileId) }))
        .filter((entry) => Boolean(entry.src))
        .map(({ tileId, src }) => ensureImage(tileId, src));
    await Promise.all(promises);
}
export function requestTileImages(tileIds, onReady) {
    const unique = [...new Set(tileIds)];
    for (const tileId of unique) {
        const src = getTileUrl(tileId);
        if (!src || cache.has(tileId)) {
            continue;
        }
        void ensureImage(tileId, src, onReady);
    }
}
export async function preloadMarker() {
    if (!cache.has("__party_marker__")) {
        const img = await loadImage(resolveTileAssetUrl(createOverlayAssetStoragePath("Pins - You're Here, red (star).png")));
        cache.set("__party_marker__", img);
    }
}
export function getTileImage(tileId) {
    return cache.get(tileId);
}
export function getPartyMarker() {
    return cache.get("__party_marker__");
}
export function requestCoastOverlayImages(filenames, onReady) {
    const unique = [...new Set(filenames)];
    for (const filename of unique) {
        const cacheKey = `coast:${filename}`;
        if (cache.has(cacheKey)) {
            continue;
        }
        void ensureImage(cacheKey, resolveTileAssetUrl(createOverlayAssetStoragePath("coast", filename)), onReady);
    }
}
export async function preloadCoastOverlayImages(filenames) {
    const unique = [...new Set(filenames)];
    await Promise.all(unique.map((filename) => ensureImage(`coast:${filename}`, resolveTileAssetUrl(createOverlayAssetStoragePath("coast", filename)))));
}
export function getCoastImage(filename) {
    return cache.get(`coast:${filename}`);
}
export function requestRoadOverlayImages(filenames, onReady) {
    const unique = [...new Set(filenames)];
    for (const filename of unique) {
        const cacheKey = `road:${filename}`;
        if (cache.has(cacheKey)) {
            continue;
        }
        void ensureImage(cacheKey, resolveTileAssetUrl(createOverlayAssetStoragePath("roads", filename)), onReady);
    }
}
export async function preloadRoadOverlayImages(filenames) {
    const unique = [...new Set(filenames)];
    await Promise.all(unique.map((filename) => ensureImage(`road:${filename}`, resolveTileAssetUrl(createOverlayAssetStoragePath("roads", filename)))));
}
export function getRoadImage(filename) {
    return cache.get(`road:${filename}`);
}
export function requestDecorationImages(decorations, onReady) {
    for (const decoration of decorations) {
        const cacheKey = `decoration:${decoration.id}`;
        if (!decoration.url || cache.has(cacheKey)) {
            continue;
        }
        void ensureImage(cacheKey, decoration.url, onReady);
    }
}
export async function preloadDecorationImages(decorations) {
    await Promise.all(decorations
        .filter((decoration) => Boolean(decoration.url))
        .map((decoration) => ensureImage(`decoration:${decoration.id}`, decoration.url)));
}
export function getDecorationImage(decorationId) {
    return cache.get(`decoration:${decorationId}`);
}
export const TITLE_BANNER_FILENAMES = {
    left: "Title Banner 6 (flex) left.png",
    mid: "Title Banner 6 (flex) mid chunk.png",
    right: "Title Banner 6 (flex) right.png",
};
const TITLE_BANNER_PARTS = ["left", "mid", "right"];
function titleBannerCacheKey(part) {
    return `title-banner:${part}`;
}
export function requestTitleBannerImages(onReady) {
    for (const part of TITLE_BANNER_PARTS) {
        const cacheKey = titleBannerCacheKey(part);
        if (cache.has(cacheKey)) {
            continue;
        }
        void ensureImage(cacheKey, resolveTileAssetUrl(createOverlayAssetStoragePath(TITLE_BANNER_FILENAMES[part])), onReady);
    }
}
export async function preloadTitleBannerImages() {
    await Promise.all(TITLE_BANNER_PARTS.map((part) => ensureImage(titleBannerCacheKey(part), resolveTileAssetUrl(createOverlayAssetStoragePath(TITLE_BANNER_FILENAMES[part])))));
}
export function getTitleBannerImage(part) {
    return cache.get(titleBannerCacheKey(part));
}
export function collectMapPreloadAssets(hexes, decorations, hexKeys) {
    const tileIds = new Set();
    const coastFilenames = new Set();
    const roadFilenames = new Set();
    for (const key of hexKeys) {
        const hex = hexes[key];
        if (!hex)
            continue;
        const tileId = resolveTileId({
            tileId: hex.tileId,
            tileFilename: hex.tileFilename,
            tileSetId: hex.tileSetId,
        });
        if (tileId) {
            tileIds.add(tileId);
        }
        for (const overlay of getCoastOverlaysForHex(hex.q, hex.r, hexes)) {
            coastFilenames.add(overlay.filename);
        }
        if (hex.roadEdges && hex.roadEdges.length > 0) {
            for (const overlay of getRoadOverlaysForEdges(hex.roadEdges, key)) {
                roadFilenames.add(overlay.filename);
            }
        }
    }
    const hexKeySet = new Set(hexKeys);
    const decorationAssets = new Map();
    const decorationValues = decorations ? Object.values(decorations) : [];
    for (const decoration of decorationValues) {
        const decorationKey = `${decoration.q},${decoration.r}`;
        if (!hexKeySet.has(decorationKey))
            continue;
        if (decorationAssets.has(decoration.decorationId))
            continue;
        const catalogDecoration = getCatalogDecorationById(decoration.decorationId);
        if (!catalogDecoration?.url)
            continue;
        decorationAssets.set(decoration.decorationId, {
            id: decoration.decorationId,
            url: catalogDecoration.url,
        });
    }
    return {
        tileIds: Array.from(tileIds),
        coastFilenames: Array.from(coastFilenames),
        roadFilenames: Array.from(roadFilenames),
        decorations: Array.from(decorationAssets.values()),
    };
}
export async function preloadMapAssets(assets, onProgress) {
    const entries = [];
    for (const tileId of assets.tileIds) {
        const src = getTileUrl(tileId);
        if (!src)
            continue;
        entries.push({ cacheKey: tileId, src });
    }
    for (const filename of assets.coastFilenames) {
        entries.push({
            cacheKey: `coast:${filename}`,
            src: resolveTileAssetUrl(createOverlayAssetStoragePath("coast", filename)),
        });
    }
    for (const filename of assets.roadFilenames) {
        entries.push({
            cacheKey: `road:${filename}`,
            src: resolveTileAssetUrl(createOverlayAssetStoragePath("roads", filename)),
        });
    }
    for (const decoration of assets.decorations) {
        if (!decoration.url)
            continue;
        entries.push({ cacheKey: `decoration:${decoration.id}`, src: decoration.url });
    }
    const total = entries.length;
    if (total === 0) {
        onProgress?.(0, 0);
        return;
    }
    let loaded = 0;
    onProgress?.(loaded, total);
    await Promise.allSettled(entries.map((entry) => ensureImage(entry.cacheKey, entry.src).finally(() => {
        loaded += 1;
        onProgress?.(loaded, total);
    })));
}
